import '../order_movement.css'

import { SyncOutlined } from '@ant-design/icons'
import { useOrderMovements } from '@api/useOrderMovements'
import { ExpandCollapseButtons } from '@components/_SiteWide/ExpandCollapseButtons'

import { DeliveryTicketModal } from '@components/OrderMovements/OrderMovements/MovementDetail/Drops/DeliveryTicketModal'
import { MovementDetail } from '@components/OrderMovements/OrderMovements/MovementDetail/MovementDetail'
import { OrderModal } from '@components/OrderWizard/OrderModal'
import { useLockedOrders } from '@hooks/useLockedOrders'
import { GraviGrid, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useLocalStorage } from '@gravitate-js/the-armory'
import { useUserSettings } from '@hooks/settings'
import { useModelConfig } from '@hooks/useModelConfig'
import { notification, Select } from 'antd'
import { addDays, subDays } from 'date-fns'
import moment from 'moment'
import queryString from 'query-string'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useSecurityContext } from '../../Navigation/SecurityContext'
import { getColumnDefs } from './columnDefs'
import { EbolMatchingProvider } from './EbolMatching/EbolMatchingProvider'
import { UnrankedEbolButton } from './EbolMatching/UnrankedEbolButton'
import { useCanViewEbols } from './EbolMatching/Hooks/useCanViewEbols'
import { DateRangeFilter } from '@/components/_SiteWide/DateRangeFilter'
import { gridViewManager } from '@/utils/grid'

const store = window.localStorage
const { Option } = Select
function OrderMovements({ isCompact, storeNumber }) {
  const storageKey = isCompact ? 'order-movements-compact' : 'order-movements-report'
  const { updateSetting } = useUserSettings()
  const { value: movementsDayRange, setValue: settingMovementsDayRange } = useLocalStorage(`${storageKey}-day-range`, {
    back: -3,
    ahead: 1,
  })
  const setMovementsDayRange = (newRange) => {
    settingMovementsDayRange(newRange)
    updateSetting(`${storageKey}-day-range`, newRange)
  }
  const [filters, setFilters] = useState({
    From: moment(subDays(new Date(), movementsDayRange.back ? Math.abs(parseInt(movementsDayRange.back)) : 3)).startOf(
      'day'
    ),
    To: moment(addDays(new Date(), movementsDayRange.ahead ? parseInt(movementsDayRange.ahead) : 1)).endOf('day'),
  })
  const [hasBeenExpanded, setHasBeenExpanded] = useState(false)
  const [view, setView] = useState()
  const [showingModal, setShowingModal] = useState()
  const [showingTicketModal, setShowingTicketModal] = useState(false)
  const [deliveryTicketData, setDeliveryTicketData] = useState()
  const [selectedOrderId, setSelectedOrderId] = useState()
  const [ticketCache, setTicketCache] = useState({})
  const gridAPIRef = useRef()
  const { scopes } = useSecurityContext()
  const canViewOrder = scopes.order_movements?.view || scopes.order_movements?.edit || scopes?.create_order
  const { config } = useModelConfig()
  const { useOrderMovementsQuery } = useOrderMovements()
  const { data: orderMovements, isLoading, isPending } = useOrderMovementsQuery(filters, view, isCompact)

  const hiddenFilterKeys = ['bol to', 'bol from', 'to', 'from']
  const backOfficeErp = useMemo(
    () => config?.backoffice_erp_integration?.validation_enabled && config?.backoffice_erp_integration?.file_enabled,
    [config]
  )
  const { lockedOrders } = useLockedOrders()
  useEffect(() => {
    if (storeNumber) {
      setFilters((prevState) => {
        return { ...prevState, Store: storeNumber }
      })
    }
  }, [storeNumber])

  useEffect(() => {
    const currentView = store.getItem(`current-${storageKey}-view`) // 'current_order_movement_view'
    const orderNumber = queryString.parse(window.location.search).order_number
    const newFilters = { ...filters }
    if (storeNumber) {
      newFilters.Store = storeNumber
    }
    if (orderNumber) {
      newFilters.Order = orderNumber
      setView('all')
    }
    setFilters(newFilters)
    setView(currentView || 'pending')
  }, [])

  useEffect(() => {
    const orderNumber = store.getItem('orderMovements_redirected_order_number')
    const redirectMarket = store.getItem('orderMovements_redirected_market')
    if (orderNumber && redirectMarket) {
      store.removeItem('orderMovements_redirected_order_number')
      store.removeItem('orderMovements_redirected_market')
      notification.open({
        message: 'Market Changed',
        description: (
          <Vertical>
            <Texto>
              Order {orderNumber} is in {redirectMarket} market.
            </Texto>
            <Texto> Market switched to {redirectMarket}.</Texto>
          </Vertical>
        ),
        icon: <SyncOutlined style={{ color: 'var(--theme-color-2)' }} />,
      })
    }
  }, [])
  const updateView = (value) => {
    setView(value.toLowerCase())
    store.setItem(`current-${storageKey}-view`, value.toLowerCase())
    updateSetting(`current-${storageKey}-view`, value.toLowerCase())
  }
  const showModal = (orderId) => {
    if (canViewOrder) {
      setSelectedOrderId(orderId)
      setShowingModal(true)
    }
  }
  const closeModal = () => {
    setShowingModal(false)
    setSelectedOrderId(undefined)
  }

  const getDatesFromStorage = () => {
    if (movementsDayRange) {
      return {
        From: moment().add(movementsDayRange.back, 'days').startOf('day'),
        To: moment().add(movementsDayRange.ahead, 'days').endOf('day'),
      }
    }
    return null
  }

  const canViewEbol = useCanViewEbols()

  const columnDefs = useMemo(
    () =>
      backOfficeErp !== 'undefined'
        ? getColumnDefs(showModal, backOfficeErp, canViewOrder, isCompact, lockedOrders, canViewEbol)
        : [],
    [backOfficeErp, canViewOrder, isCompact, lockedOrders, canViewEbol]
  )
  const getRowId = useCallback((row) => row.data?.id, [])
  const agPropOverrides = useMemo(
    () => ({
      getRowId,
      rowSelection: 'single',
      rowGroupPanelShow: isCompact ? 'onlyWhenGrouping' : 'always',
    }),
    [isCompact]
  )

  return (
    <EbolMatchingProvider>
      <OrderModal showingModal={showingModal} closeModal={closeModal} selectedOrder={selectedOrderId} />
      <DeliveryTicketModal
        isVisible={showingTicketModal}
        onCancel={() => {
          setShowingTicketModal(false)
        }}
        onOk={() => {
          setShowingTicketModal(false)
        }}
        data={deliveryTicketData}
        ticketCache={ticketCache}
        setTicketCache={setTicketCache}
      />

      <GraviGrid
        controlBarProps={{
          title: 'Order Movements',
          actionButtons: (
            <ActionButtons
              filters={filters}
              setFilters={setFilters}
              view={view}
              updateView={updateView}
              gridAPIRef={gridAPIRef}
              isCompact={isCompact}
              movementsDayRange={movementsDayRange}
              setMovementsDayRange={setMovementsDayRange}
              getDatesFromStorage={getDatesFromStorage}
              hasBeenExpanded={hasBeenExpanded}
              setHasBeenExpanded={setHasBeenExpanded}
            />
          ),
          serverParams: isCompact ? undefined : orderMovements?.params,
          setFilters,
          filters,
          hiddenFilterKeys: isCompact ? [...hiddenFilterKeys, 'store'] : hiddenFilterKeys,
          defaultFilter: { From: subDays(new Date(), 2), To: addDays(new Date(), 2) },
        }}
        externalRef={gridAPIRef}
        loading={isPending || backOfficeErp === undefined || isLoading}
        agPropOverrides={agPropOverrides}
        columnDefs={columnDefs}
        detailCellRenderer={MovementDetail}
        detailCellRendererParams={{
          setShowingTicketModal,
          setDeliveryTicketData,
          gridAPIRef,
        }}
        masterDetail
        storageKey={storageKey}
        onGridConfigChanged={(config) => updateSetting(`gridConfig::${storageKey}`, config)}
        rowData={orderMovements?.rows}
        detailRowAutoHeight
        onRowGroupOpened={(params) => {
          if (params.node?.expanded) {
            setHasBeenExpanded(true)
          } else {
            let x = false
            params.api.forEachNodeAfterFilter((node) => {
              if (node.expanded) x = true
            })
            setHasBeenExpanded(x)
          }
        }}
        {...gridViewManager(storageKey, updateSetting)}
      />
    </EbolMatchingProvider>
  )
}
function ActionButtons({
  view,
  updateView,
  setFilters,
  filters,
  gridAPIRef,
  isCompact,
  movementsDayRange,
  setMovementsDayRange,
  getDatesFromStorage,
  hasBeenExpanded,
  setHasBeenExpanded,
}) {
  return (
    <div className='movement-status-container'>
      <UnrankedEbolButton />
      {!isCompact && (
        <ExpandCollapseButtons gridAPIRef={gridAPIRef} isExpanded={hasBeenExpanded} setExpanded={setHasBeenExpanded} />
      )}
      <Texto appearance='medium' category='label'>
        STATUS
      </Texto>
      <Select style={{ width: 160 }} value={view} onChange={updateView}>
        <Option key='all' value='all'>
          <div className='flex items-center'>
            <span className='all dot mr-1' />
            <span>All</span>
          </div>
        </Option>

        <Option key='pending' value='pending'>
          <div className='flex items-center'>
            <span className='all_open dot mr-1' />
            <span>Pending</span>
          </div>
        </Option>
        <Option key='added' value='added'>
          <div className='flex items-center'>
            <span className='completed dot mr-2' />
            <span>Added</span>
          </div>
        </Option>
        <Option key='invalid' value='invalid'>
          <div className='flex items-center'>
            <span className='invalid dot mr-2' />
            <span>Invalid</span>
          </div>
        </Option>
      </Select>
      {filters && (
        <DateRangeFilter
          setCurrentFilters={setFilters}
          currentFilters={filters}
          getFromStorage={getDatesFromStorage}
          dayRangeStorage={movementsDayRange}
          setDayRangeStorage={setMovementsDayRange}
        />
      )}
    </div>
  )
}

export default OrderMovements
